import { SlicePipe } from '@angular/common';
import { AfterViewInit, Component, forwardRef, Input, OnDestroy, output, viewChildren } from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  ControlValueAccessor,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  NgForm,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';

import { Subscription, throttleTime } from 'rxjs';

import { MarkAllTouchedDirective } from '../directives/mark-all-touched.directive';
import { InputComponent } from '../input/input.component';

@Component({
  selector: 'bn-web-ui-two-factor-code',
  templateUrl: './two-factor-code.component.html',
  styleUrls: ['./two-factor-code.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => TwoFactorCodeComponent),
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => TwoFactorCodeComponent),
      multi: true,
    },
  ],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
  imports: [FormsModule, MarkAllTouchedDirective, ReactiveFormsModule, InputComponent, SlicePipe],
})
export class TwoFactorCodeComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  @Input()
  public autofocus = false;

  @Input()
  public loading = false;

  public readonly submit = output<null>();

  readonly inputs = viewChildren<HTMLInputElement>('input');

  get code(): UntypedFormArray {
    return this.twoFactorCodeForm.controls['code'] as UntypedFormArray;
  }

  public twoFactorCodeForm = new UntypedFormGroup({
    code: new UntypedFormArray([
      new UntypedFormControl('', [Validators.required, Validators.maxLength(1), Validators.pattern('[0-9]')]),
      new UntypedFormControl('', [Validators.required, Validators.maxLength(1), Validators.pattern('[0-9]')]),
      new UntypedFormControl('', [Validators.required, Validators.maxLength(1), Validators.pattern('[0-9]')]),
      new UntypedFormControl('', [Validators.required, Validators.maxLength(1), Validators.pattern('[0-9]')]),
      new UntypedFormControl('', [Validators.required, Validators.maxLength(1), Validators.pattern('[0-9]')]),
      new UntypedFormControl('', [Validators.required, Validators.maxLength(1), Validators.pattern('[0-9]')]),
    ]),
  });

  private nativeInputs!: readonly HTMLInputElement[];
  private subscriptions: Subscription = new Subscription();

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public ngAfterViewInit(): void {
    const inputs = this.inputs();
    if (inputs) {
      this.nativeInputs = inputs;
    }

    this.subscriptions.add(
      this.twoFactorCodeForm.get(['code', 0])?.valueChanges.subscribe((value = '') => {
        // Note: in case of autofill safari
        if (value.length === 6) {
          this.onPasteHandler(value);
        }
      }),
    );
  }

  private onChange: (value: string) => void = () => undefined;
  private onTouch: () => void = () => undefined;

  public writeValue(value: string[]): void {
    if (!value) {
      this.twoFactorCodeForm.reset();
    }

    this.code.controls.forEach((control: AbstractControl, index: number) => {
      const newValue = value && value[index] ? value[index] : null;

      control.setValue(newValue);
    });
  }

  public registerOnChange(fn: (value: string[]) => void): void {
    this.subscriptions.add(this.twoFactorCodeForm.valueChanges.pipe(throttleTime(50, undefined, { leading: false, trailing: true })).subscribe(fn));
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }

  public validate(): false | { invalid: boolean } {
    const formControlArray = this.twoFactorCodeForm.get('code') as UntypedFormArray;
    const formIsValid = formControlArray.controls.filter((formControl) => formControl.valid)?.length === formControlArray.controls?.length;

    return (
      !formIsValid && {
        invalid: true,
      }
    );
  }

  private isCharDigit(char: string): boolean {
    return !!char.trim() && Number(char) >= 0 && Number(char) <= 9;
  }

  private focusExact(i: number): void {
    this.nativeInputs[i].focus();
  }

  public onBackspace(event: Event, i: number): void {
    if (!this.nativeInputs || !this.nativeInputs[i]) return;

    if (this.twoFactorCodeForm.get(['code', i])?.value) {
      this.twoFactorCodeForm.get(['code', i])?.setValue('');
    } else {
      this.focusPrev(i);
    }
  }

  public validateAndFocusNext(key: string, currentInputIndex: number): void {
    if (!key?.match('[0-9]')) return;

    // Note override value with last char
    this.twoFactorCodeForm.get(['code', currentInputIndex])?.setValue(key);

    this.focusNext(currentInputIndex);
  }

  public focusNext(i: number): void {
    if (i === 5) {
      this.focusExact(0);
    }

    if (i > 5) return;

    this.focusExact(i + 1 < this.nativeInputs.length ? i + 1 : 0);
  }

  public focusPrev(i: number): void {
    if (i <= 0) return;
    this.focusExact(i - 1 < 0 ? this.nativeInputs.length - 1 : i - 1);
  }

  public onInputPaste(): boolean {
    return false;
  }

  public onInput(event: Event, i: number): void {
    const input = event.target as HTMLInputElement;
    const data = input.value;

    if (!data) return;

    if (data.length === 6 && [...data].filter((char) => !isNaN(+char)).length === data.length) {
      // is multi-char input from android keyboard suggestion
      this.onPasteHandler(data);
      return;
    }

    this.validateAndFocusNext((event as InputEvent)?.data || '', i);
  }

  private onPasteHandler(clipboardValue: string): void {
    [...clipboardValue].map((value: string, index: number) => {
      const formControl = this.twoFactorCodeForm.get(['code', index]);

      if (formControl && value && this.isCharDigit(value)) {
        formControl.setValue(value, { emitEvent: true });
      } else {
        formControl?.markAsUntouched();
        formControl?.setErrors(null);
      }
    });
  }

  public onPaste(event?: ClipboardEvent): void {
    if (!event?.clipboardData) return;

    this.onPasteHandler(event.clipboardData.getData('text'));
  }
}
